import React from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { IncludedGuestEntry } from '~/types/roomPolicy';

interface RoomPolicyIncludedGuestsTableProps {
  title: string;
  addButtonLabel: string;
  items: Array<IncludedGuestEntry>;
  onChange: (items: Array<IncludedGuestEntry>) => void;
}

const EMPTY_ENTRY: IncludedGuestEntry = {
  adults: 0,
  children: 0,
  infants: 0,
  teenagers: 0,
};

export default function RoomPolicyIncludedGuestsTable({
  title,
  addButtonLabel,
  items,
  onChange,
}: RoomPolicyIncludedGuestsTableProps) {
  const handleFieldChange = (index: number, field: keyof IncludedGuestEntry, value: number) => {
    const updatedItems = [...items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    onChange(updatedItems);
  };

  const handleAdd = () => {
    onChange([...items, { ...EMPTY_ENTRY }]);
  };

  const handleRemove = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Adults</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Children</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Infants</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Teenagers</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: 60 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, index) => (
              <TableRow key={index}>
                <TableCell>
                  <TextField
                    type="number"
                    inputProps={{ min: 0, step: 1 }}
                    size="small"
                    value={item.adults}
                    onChange={(e) => handleFieldChange(index, 'adults', parseInt(e.target.value) || 0)}
                    fullWidth
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    inputProps={{ min: 0, step: 1 }}
                    size="small"
                    value={item.children}
                    onChange={(e) => handleFieldChange(index, 'children', parseInt(e.target.value) || 0)}
                    fullWidth
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    inputProps={{ min: 0, step: 1 }}
                    size="small"
                    value={item.infants}
                    onChange={(e) => handleFieldChange(index, 'infants', parseInt(e.target.value) || 0)}
                    fullWidth
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    inputProps={{ min: 0, step: 1 }}
                    size="small"
                    value={item.teenagers}
                    onChange={(e) => handleFieldChange(index, 'teenagers', parseInt(e.target.value) || 0)}
                    fullWidth
                  />
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleRemove(index)} color="error">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Button size="small" startIcon={<AddIcon />} onClick={handleAdd} sx={{ mt: 1 }}>
        {addButtonLabel}
      </Button>
    </Box>
  );
}
